import Konva from 'konva'
import { VisualShape } from 'app/frontend/types/custom/VisualPlan'
import { DEFAULT_POINTER_SIZE, MIN_SIZE } from '../constants'
import { reflowTextGroup } from './textLayout'

type Dims = {
  width: number
  height: number
}

export const applyTransform = (
  shape: VisualShape,
  node: Konva.Node,
  dims: Dims,
): VisualShape => {
  const { width, height } = dims

  if (shape.type === 'text') {
    const group = node as Konva.Group
    reflowTextGroup(group)
    return {
      ...shape,
      x: group.x() / width,
      y: group.y() / height,
      width: group.width() / width,
      rotation: group.rotation(),
    }
  }

  const scaleX = node.scaleX()
  const scaleY = node.scaleY()
  node.scaleX(1)
  node.scaleY(1)

  if (shape.type === 'rect') {
    const nextWidth = Math.max(MIN_SIZE, node.width() * scaleX)
    const nextHeight = Math.max(MIN_SIZE, node.height() * scaleY)
    return {
      ...shape,
      x: node.x() / width,
      y: node.y() / height,
      width: nextWidth / width,
      height: nextHeight / height,
      rotation: node.rotation(),
    }
  }

  if (shape.type === 'icon') {
    const uniform = Math.max(Math.abs(scaleX), Math.abs(scaleY))
    const nextWidth = Math.max(MIN_SIZE, node.width() * uniform)
    const nextHeight = Math.max(MIN_SIZE, node.height() * uniform)
    return {
      ...shape,
      x: node.x() / width,
      y: node.y() / height,
      width: nextWidth / width,
      height: nextHeight / height,
      rotation: node.rotation(),
    }
  }

  if (shape.type === 'circle') {
    const ellipse = node as Konva.Ellipse
    const nextRadiusX = Math.max(MIN_SIZE, ellipse.radiusX() * scaleX)
    const nextRadiusY = Math.max(MIN_SIZE, ellipse.radiusY() * scaleY)
    return {
      ...shape,
      x: node.x() / width,
      y: node.y() / height,
      radius_x: nextRadiusX / width,
      radius_y: nextRadiusY / width,
      rotation: node.rotation(),
    }
  }

  if (shape.type === 'triangle') {
    const nextWidth = Math.max(MIN_SIZE, node.width() * scaleX)
    const nextHeight = Math.max(MIN_SIZE, node.height() * scaleY)
    return {
      ...shape,
      x: node.x() / width,
      y: node.y() / height,
      width: nextWidth / width,
      height: nextHeight / height,
      rotation: node.rotation(),
    }
  }

  if (shape.type === 'pencil') {
    const pencilShape = shape as Extract<VisualShape, { type: 'pencil' }>
    return {
      ...shape,
      x: node.x() / width,
      y: node.y() / height,
      points: pencilShape.points.map((point, i) =>
        i % 2 === 0 ? point * scaleX : point * scaleY,
      ),
      rotation: node.rotation(),
    }
  }

  if (shape.type === 'line') {
    const lineShape = shape as Extract<VisualShape, { type: 'line' }>
    return {
      ...shape,
      x: node.x() / width,
      y: node.y() / height,
      points: lineShape.points.map((point, i) =>
        i % 2 === 0 ? point * scaleX : point * scaleY,
      ),
      rotation: node.rotation(),
    }
  }

  const arrowShape = shape as Extract<VisualShape, { type: 'arrow' }>
  return {
    ...shape,
    x: node.x() / width,
    y: node.y() / height,
    points: arrowShape.points.map((point, i) =>
      i % 2 === 0 ? point * scaleX : point * scaleY,
    ),
    pointer_length:
      (arrowShape.pointer_length ?? DEFAULT_POINTER_SIZE) * scaleX,
    pointer_width: (arrowShape.pointer_width ?? DEFAULT_POINTER_SIZE) * scaleY,
    rotation: node.rotation(),
  }
}
